'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button, Checkbox, Field, Input } from '@pxsite/ui'

function AiFace({ eyesClosed }: { eyesClosed: boolean }) {
  const ref = useRef<SVGSVGElement>(null)
  const [gaze, setGaze] = useState({ x: 0, y: 0 })
  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      const box = ref.current?.getBoundingClientRect()
      if (!box) return
      const dx = (event.clientX - (box.left + box.width / 2)) / (box.width / 2)
      const dy = (event.clientY - (box.top + box.height / 2)) / (box.height / 2)
      const clamp = (v: number) => Math.max(-1, Math.min(1, v))
      setGaze({ x: clamp(dx) * 5.5, y: clamp(dy) * 4 })
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [])
  const eye = (cx: number) => (
    <g key={cx}>
      <ellipse cx={cx} cy={104} rx={17} ry={eyesClosed ? 1.4 : 17} fill="#131211" />
      {!eyesClosed && (<><circle cx={cx + gaze.x} cy={104 + gaze.y} r={6.4} fill="#faf9f8" /><circle cx={cx + gaze.x + 2.2} cy={104 + gaze.y - 2.6} r={1.9} fill="#131211" /></>)}
    </g>
  )
  return (
    <svg ref={ref} viewBox="0 0 260 260" role="img" aria-label="PXSite AI 助手示意图" className="w-full max-w-[300px]">
      <g stroke="#131211" strokeWidth="1.4" fill="none">
        <path d="M130 34v20" /><circle cx="130" cy="27" r="6" fill="#c2652a" />
        <rect x="46" y="54" width="168" height="126" rx="6" fill="#ffffff" />
        <rect x="62" y="70" width="136" height="70" rx="4" fill="#f6e8dc" />
        <path d="M96 180v16H74a10 10 0 0 0-10 10v14h132v-14a10 10 0 0 0-10-10h-22v-16" fill="#ffffff" />
        <path d="M46 96H30v28h16M214 96h16v28h-16" />
      </g>
      {[104, 156].map(eye)}
      <path d={eyesClosed ? 'M114 160h32' : 'M116 158c5 6 23 6 28 0'} stroke="#131211" strokeWidth="1.6" strokeLinecap="round" fill="none" />
      <g fontFamily="Geist Mono, monospace" fontSize="8" fill="#78736e" letterSpacing="1.6">
        <text x="130" y="234" textAnchor="middle">{eyesClosed ? '已隐藏' : 'PXSITE 管理员'}</text>
      </g>
    </svg>
  )
}

export default function AdminLoginPage() {
  const router = useRouter()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [typing, setTyping] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [remember, setRemember] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  const submit = useCallback(async () => {
    if (!username.trim() || !password.trim()) { setError('请输入用户名和密码'); return }
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }) })
      const data = await res.json()
      if (data.success) { router.push('/admin/dashboard') }
      else { setError(data.error || '登录失败') }
    } catch { setError('网络错误，请稍后重试') }
    finally { setLoading(false) }
  }, [username, password, router])

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <aside className="relative hidden flex-col justify-between border-r border-line bg-sunken px-10 py-9 lg:flex">
        <div aria-hidden className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: 'linear-gradient(to right, #e4e1dd 1px, transparent 1px), linear-gradient(to bottom, #e4e1dd 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <p className="relative font-mono text-[13px] tracking-[0.16em] text-ink-3 uppercase">PXSite · 多租户建站平台</p>
        <div className="relative flex justify-center"><AiFace eyesClosed={typing} /></div>
        <div className="relative max-w-sm">
          <p className="text-[17px] leading-relaxed font-medium text-ink">为 1,240 家企业提供网站托管、域名解析与证书自动续期。</p>
          <p className="mt-1.5 font-mono text-[13px] text-ink-3">SLA 99.98% · 节点 6 · 平均构建耗时 11.4s</p>
        </div>
      </aside>
      <main className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-[400px]">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-xs bg-accent-fill font-mono text-[15px] font-semibold text-ink">P</span>
            <span className="font-mono text-[15px] tracking-[0.14em] text-ink uppercase">pxsite.site</span>
          </div>
          <h1 className="mt-8 text-[26px] leading-tight font-semibold tracking-[-0.015em]">管理员登录</h1>
          <p className="mt-1 text-[14.5px] text-ink-3">平台管理员入口</p>
          <form className="mt-7 flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); submit() }}>
            <Field label="用户名"><Input value={username} autoComplete="username" placeholder="请输入用户名" onChange={(e) => setUsername(e.target.value)} className="h-11 text-[16px]" /></Field>
            <Field label="密码"><div className="relative"><Input type={showPassword ? 'text' : 'password'} value={password} autoComplete="current-password" placeholder="请输入密码" onChange={(e) => setPassword(e.target.value)} onFocus={() => setTyping(true)} onBlur={() => setTyping(false)} className="h-11 text-[16px] pr-10" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-0 h-11 w-10 flex items-center justify-center text-ink-3 hover:text-ink" tabIndex={-1}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d={showPassword ? 'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' : 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'} /><circle cx="12" cy="12" r={showPassword ? '0' : '3'} /><line x1="1" y1="1" x2="23" y2="23" strokeWidth={showPassword ? '1.5' : '0'} /></svg></button></div></Field>
            {error && <p role="alert" className="font-mono text-[14px] text-danger">{error}</p>}
            <Checkbox checked={remember} onChange={setRemember} label={<span>记住密码<span className="ml-1.5 font-mono text-[13px] text-ink-3">180天内免密登录</span></span>} />
            <Button type="submit" variant="primary" className="mt-1 h-11 w-full text-[16px]" disabled={loading}>{loading ? '登录中...' : '登 录'}</Button>
          </form>
          <p className="mt-6 border-t border-line pt-4 font-mono text-[13px] text-ink-3">本次登录将被记录</p>
        </div>
      </main>
    </div>
  )
}
